import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { checkIfTokenIsValid } from '../authStorage';
import {
  setInitialState,
  clearNotifications,
  setReadAllBefore,
  setUnseenCount,
} from '../redux/slices/notifications.slice';
import { setFirebaseUid } from '../redux/slices/user.slice';
import { AppDispatch, RootState, store } from '../redux/store';
import firebaseCleanup from '../redux/thunks/firebaseCleanup';
import { DEFAULT_FILTER_KEY } from '../redux/types/notifications';
import { useReconnectTokenMutation } from '../services/api/user.api';
import { onFirebaseAuthStateChanged, signInWithFirebaseToken } from '../services/firebase/firebaseAuth';
import {
  loadInitialNotificationsWithFilter,
  startReadAllBeforeListener,
  startUnseenCountListener,
  stopReadAllBeforeListener,
  stopUnseenCountListener,
  stopFilteredNotificationsListener,
  parseFilterKey,
} from '../services/firebase/notificationsService';

/**
 * Global hook that manages Firebase authentication state and notifications listener.
 * Should be used once at the app root level (App.tsx).
 *
 * Flow:
 * 1. Listens to Firebase auth state changes
 * 2. When user authenticates: saves uid to Redux, loads initial notifications
 * 3. When user signs out: clears uid, stops listener, clears notifications
 */
const useFirebaseAuth = (): void => {
  const dispatch = useDispatch<AppDispatch>();
  const [requestFirebaseReconnectToken] = useReconnectTokenMutation();
  const accessToken = useSelector((state: RootState) => state.user.accessToken);
  const firebaseUid = useSelector((state: RootState) => state.user.firebaseUid);
  const isListenerSetup = useRef(false);
  const isMounted = useRef(true);
  // Prevent duplicate reconnect attempts for the same app session token.
  // - isReconnecting: blocks concurrent reconnect requests
  // - hasTriedReconnect: blocks retry loops for the same token
  // - reconnectAccessToken: tracks which token the last attempt belonged to
  const isReconnecting = useRef(false);
  const hasTriedReconnect = useRef(false);
  const reconnectAccessToken = useRef<string | null>(null);

  useEffect(() => {
    if (!accessToken) {
      hasTriedReconnect.current = false;
      isReconnecting.current = false;
      reconnectAccessToken.current = null;
      return;
    }

    if (!isReconnecting.current && reconnectAccessToken.current !== accessToken) {
      hasTriedReconnect.current = false;
    }
  }, [accessToken]);

  // Listen to Firebase auth state changes (runs once on mount)
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    if (!isListenerSetup.current) {
      isListenerSetup.current = true;
      unsubscribe = onFirebaseAuthStateChanged((usr) => {
        if (usr) {
          dispatch(setFirebaseUid(usr.uid));
        } else {
          dispatch(setFirebaseUid(null));
        }
      });
    }

    return (): void => {
      if (unsubscribe) {
        unsubscribe();
        isListenerSetup.current = false;
      }
    };
  }, [dispatch]);

  // Manage notifications based on firebaseUid
  useEffect(() => {
    isMounted.current = true;

    const hasValidUserSession = checkIfTokenIsValid(store.getState().user);

    if (firebaseUid) {
      hasTriedReconnect.current = false;

      // Start unseen count listener immediately (before loading notifications)
      startUnseenCountListener(firebaseUid, (count) => {
        if (!isMounted.current) return;
        dispatch(setUnseenCount(count));
      });
      startReadAllBeforeListener(firebaseUid, (readAllBefore) => {
        if (!isMounted.current) return;
        dispatch(setReadAllBefore(readAllBefore));
      });

      // Parse default filter
      const { domain, status } = parseFilterKey(DEFAULT_FILTER_KEY);

      // Load initial notifications for default filter
      loadInitialNotificationsWithFilter(firebaseUid, domain, status)
        .then(({ notifications, cursor, hasMore, unseenCount, readAllBefore }) => {
          if (!isMounted.current) return;
          dispatch(
            setInitialState({
              filterKey: DEFAULT_FILTER_KEY,
              notifications,
              cursor,
              hasMore,
              unseenCount,
              readAllBefore,
            })
          );
        })
        .catch(() => {
          if (!isMounted.current) return;
          dispatch(
            setInitialState({
              filterKey: DEFAULT_FILTER_KEY,
              notifications: {},
              cursor: null,
              hasMore: false,
              unseenCount: 0,
              readAllBefore: null,
            })
          );
        });
    } else {
      stopFilteredNotificationsListener();
      stopReadAllBeforeListener();
      stopUnseenCountListener();
      dispatch(clearNotifications());

      if (hasValidUserSession && accessToken && !isReconnecting.current && !hasTriedReconnect.current) {
        const accessTokenAtReconnectStart = accessToken;

        hasTriedReconnect.current = true;
        isReconnecting.current = true;
        reconnectAccessToken.current = accessTokenAtReconnectStart;

        const reconnectFirebase = async (): Promise<void> => {
          try {
            const firebaseTokenResponse = await requestFirebaseReconnectToken().unwrap();

            if (!isMounted.current || !firebaseTokenResponse.firebaseToken) {
              return;
            }

            await signInWithFirebaseToken(firebaseTokenResponse.firebaseToken);
          } catch (error) {
            if (!isMounted.current) {
              return;
            }
          } finally {
            if (isMounted.current) {
              isReconnecting.current = false;

              const latestAccessToken = store.getState().user.accessToken;
              if (latestAccessToken && latestAccessToken !== accessTokenAtReconnectStart) {
                hasTriedReconnect.current = false;
              }
            }
          }
        };

        reconnectFirebase().catch(() => undefined);
      }
    }

    return (): void => {
      isMounted.current = false;
      stopFilteredNotificationsListener();
      stopReadAllBeforeListener();
      stopUnseenCountListener();
    };
  }, [firebaseUid, accessToken, dispatch, requestFirebaseReconnectToken]);
};

/**
 * Hook to handle logout from Firebase.
 * Call this in addition to your regular logout flow.
 */
export const useFirebaseLogout = (): (() => Promise<void>) => {
  const dispatch = useDispatch<AppDispatch>();

  return async () => {
    dispatch(firebaseCleanup());
  };
};

export default useFirebaseAuth;
